import React, { useState, useEffect, useCallback } from 'react';
import { Plus, Trash2, X, Clock, Search, Check, Layers, Edit2, Loader2, Download } from 'lucide-react';
import axios from 'axios';
import { API } from '@/api';
import { formatApiError } from '../../utils/apiError';
import { createTextPicker } from '../../utils/localizedContent';


const TCG_OPTIONS = [
  { value: 'pokemon',  label: 'Pokémon' },
  { value: 'magic',    label: 'Magic: The Gathering' },
  { value: 'yugioh',   label: 'Yu-Gi-Oh!' },
  { value: 'onepiece', label: 'One Piece TCG' },
  { value: 'riftbound',label: 'Riftbound' },
];

export default function AdminBoosters({ boosters, onRefresh, lang, setMessage }) {
  const [showSearch, setShowSearch] = useState(false);
  const [tcgType, setTcgType] = useState('pokemon');
  const [query, setQuery] = useState('');
  const [allSets, setAllSets] = useState([]);       // All sets for the selected TCG
  const [loadingSets, setLoadingSets] = useState(false);
  const [selected, setSelected] = useState(new Set()); // Selected set_ids
  const [importing, setImporting] = useState(false);
  const [editingBooster, setEditingBooster] = useState(null);
  const [editForm, setEditForm] = useState({ name: '', release_date: '', total_cards: '' });

  const l = createTextPicker(lang);

  // Auto-cargar todos los sets al cambiar el TCG
  const loadSets = useCallback(async (tcg) => {
    setLoadingSets(true);
    setAllSets([]);
    setSelected(new Set());
    try {
      const { data } = await axios.get(`${API}/tcg/sets`, { params: { tcg_type: tcg } });
      setAllSets(data.sets || []);
    } catch (err) {
      setMessage(formatApiError(err));
    }
    setLoadingSets(false);
  }, [setMessage]);

  useEffect(() => {
    if (showSearch) loadSets(tcgType);
  }, [tcgType, showSearch, loadSets]);

  // Filtrar por consulta
  const visibleSets = query.trim()
    ? allSets.filter(s => s.name?.toLowerCase().includes(query.toLowerCase()) || s.set_id?.toLowerCase().includes(query.toLowerCase()))
    : allSets;

  const toggleSelect = (setId) => {
    setSelected(prev => {
      const next = new Set(prev);
      if (next.has(setId)) next.delete(setId);
      else next.add(setId);
      return next;
    });
  };

  const toggleSelectAll = () => {
    const notImported = visibleSets.filter(s => !isImported(s));
    const allSelected = notImported.every(s => selected.has(s.set_id));
    setSelected(prev => {
      const next = new Set(prev);
      notImported.forEach(s => allSelected ? next.delete(s.set_id) : next.add(s.set_id));
      return next;
    });
  };

  const isImported = (set) => boosters.some(b => b.set_id === set.set_id && b.tcg_type === set.tcg_type);

  const handleImportSelected = async () => {
    const toImport = allSets.filter(s => selected.has(s.set_id) && !isImported(s));
    if (toImport.length === 0) { setMessage(l('Nada que importar', 'Nothing to import')); return; }
    setImporting(true);
    let imported = 0;
    for (const set of toImport) {
      try {
        await axios.post(`${API}/admin/boosters`, {
          set_id: set.set_id, name: set.name, set_code: set.set_code || set.set_id,
          tcg_type: set.tcg_type || tcgType, release_date: set.release_date,
          total_cards: set.total_cards, logo_url: set.logo_url,
          symbol_url: set.symbol_url, series: set.series
        });
        imported++;
      } catch { /* skip already imported */ }
    }
    setMessage(l(`${imported} booster(s) importado(s)`, `${imported} booster(s) imported`));
    setSelected(new Set());
    setImporting(false);
    onRefresh();
  };

  const handleDelete = async (boosterId) => {
    if (!window.confirm(l('¿Eliminar booster?', 'Delete booster?'))) return;
    try {
      await axios.delete(`${API}/admin/boosters/${boosterId}`);
      setMessage(l('Booster eliminado', 'Booster deleted'));
      onRefresh();
    } catch (err) { setMessage(formatApiError(err)); }
  };

  const openEdit = (b) => {
    setEditingBooster(b.booster_id);
    setEditForm({ name: b.name || '', release_date: b.release_date || '', total_cards: b.total_cards || '' });
  };

  const handleSaveEdit = async (boosterId) => {
    try {
      await axios.put(`${API}/admin/boosters/${boosterId}`, {
        name: editForm.name,
        release_date: editForm.release_date || undefined,
        total_cards: editForm.total_cards ? parseInt(editForm.total_cards) : undefined
      });
      setMessage(l('Booster actualizado', 'Booster updated'));
      setEditingBooster(null);
      onRefresh();
    } catch (err) { setMessage(formatApiError(err)); }
  };

  const iclsInput = "bg-[#0A0A0E] border border-white/10 text-white rounded-lg px-3 py-1.5 text-sm outline-none focus:border-[#C9A962]";
  const openNativeDatePicker = (event) => {
    const input = event.currentTarget;
    if (typeof input?.showPicker === 'function') input.showPicker();
  };

  return (
    <div>
      <div className="flex justify-between mb-4">
        <h2 className="text-white font-bold text-xl flex items-center gap-2">
          <Layers size={20} className="text-[#C9A962]" />
          {l('Boosters/Sets', 'Boosters/Sets')} ({boosters.length})
        </h2>
        <button onClick={() => { setShowSearch(s => !s); setSelected(new Set()); }}
          className="btn-primary px-4 py-2 text-sm flex items-center gap-2">
          {showSearch ? <X size={15} /> : <Search size={15} />}
          {showSearch ? l('Cerrar', 'Close') : l('Importar Sets', 'Import Sets')}
        </button>
      </div>

      {/* Import Panel */}
      {showSearch && (
        <div className="bg-[#12121A] border border-white/10 rounded-2xl p-5 mb-6">
          <div className="flex flex-col sm:flex-row gap-3 mb-4">
            {/* TCG Selector */}
            <div className="flex gap-1 flex-wrap">
              {TCG_OPTIONS.map(opt => (
                <button key={opt.value} onClick={() => setTcgType(opt.value)}
                  className={`px-3 py-1.5 rounded-lg text-xs font-semibold transition-colors ${
                    tcgType === opt.value ? 'bg-[#C9A962] text-[#0A1612]' : 'bg-[#1C1C28] text-[#A1A1AA] hover:text-white'
                  }`}>
                  {opt.label}
                </button>
              ))}
            </div>

            {/* Search filter */}
            <div className="flex gap-2 flex-1">
              <input value={query} onChange={e => setQuery(e.target.value)}
                placeholder={l('Filtrar por nombre...', 'Filter by name...')}
                className="flex-1 bg-[#0A0A0E] border border-white/10 rounded-xl px-4 py-2 text-white text-sm outline-none focus:border-[#C9A962]" />
            </div>
          </div>

          {/* Select all + import button */}
          {visibleSets.length > 0 && (
            <div className="flex items-center justify-between mb-3">
              <button onClick={toggleSelectAll}
                className="text-xs text-[#A1A1AA] hover:text-white flex items-center gap-1.5">
                <div className={`w-4 h-4 rounded border-2 flex items-center justify-center ${
                  visibleSets.filter(s => !isImported(s)).every(s => selected.has(s.set_id)) && visibleSets.filter(s => !isImported(s)).length > 0
                    ? 'bg-[#C9A962] border-[#C9A962]'
                    : 'border-white/30'
                }`}>
                  {visibleSets.filter(s => !isImported(s)).every(s => selected.has(s.set_id)) && visibleSets.filter(s => !isImported(s)).length > 0 && (
                    <Check size={10} className="text-[#0A1612]" strokeWidth={3} />
                  )}
                </div>
                {l('Seleccionar todos', 'Select all')} ({visibleSets.filter(s => !isImported(s)).length})
              </button>
              {selected.size > 0 && (
                <button onClick={handleImportSelected} disabled={importing}
                  className="flex items-center gap-2 px-4 py-2 bg-[#C9A962] text-[#0A1612] rounded-xl text-sm font-semibold hover:bg-[#B8984E] disabled:opacity-60">
                  {importing ? <Loader2 size={14} className="animate-spin" /> : <Download size={14} />}
                  {l(`Importar ${selected.size}`, `Import ${selected.size}`)}
                </button>
              )}
            </div>
          )}

          {/* Scrolleable set list */}
          <div className="max-h-80 overflow-y-auto rounded-xl border border-white/5 bg-[#0A0A0E]/40">
            {loadingSets ? (
              <div className="flex items-center justify-center py-10 gap-2 text-[#A1A1AA] text-sm">
                <Loader2 size={18} className="animate-spin text-[#C9A962]" />{l('Cargando sets...', 'Loading sets...')}
              </div>
            ) : visibleSets.length === 0 ? (
              <p className="text-center text-[#52525B] py-10 text-sm">{l('Sin resultados', 'No results')}</p>
            ) : (
              visibleSets.map(set => {
                const imported = isImported(set);
                const isSelected = selected.has(set.set_id);
                return (
                  <div key={set.set_id}
                    onClick={() => !imported && toggleSelect(set.set_id)}
                    className={`flex items-center gap-3 px-4 py-3 border-b border-white/5 last:border-b-0 transition-colors ${
                      imported ? 'opacity-50 cursor-default' :
                      isSelected ? 'bg-[#C9A962]/10 cursor-pointer' : 'hover:bg-[#1C1C28] cursor-pointer'
                    }`}
                  >
                    {/* Checkbox */}
                    <div className={`w-5 h-5 rounded-md border-2 flex items-center justify-center flex-shrink-0 transition-colors ${
                      imported ? 'border-[#10B981] bg-[#10B981]' :
                      isSelected ? 'border-[#C9A962] bg-[#C9A962]' : 'border-white/30'
                    }`}>
                      {(imported || isSelected) && <Check size={12} className="text-[#0A1612]" strokeWidth={3} />}
                    </div>
                    {/* Logo */}
                    {set.logo_url && (
                      <img src={set.logo_url} alt="" className="w-8 h-8 object-contain flex-shrink-0" />
                    )}
                    {/* Info */}
                    <div className="flex-1 min-w-0">
                      <p className="text-white text-sm font-medium truncate">{set.name}</p>
                      <p className="text-[#52525B] text-xs">
                        {set.set_id}
                        {set.total_cards && ` · ${set.total_cards} cards`}
                        {set.release_date && ` · ${set.release_date}`}
                      </p>
                    </div>
                    {imported && (
                      <span className="text-[#10B981] text-xs flex-shrink-0">{l('Ya importado', 'Imported')}</span>
                    )}
                  </div>
                );
              })
            )}
          </div>
        </div>
      )}

      {/* Existing boosters list */}
      {boosters.length === 0 ? (
        <p className="text-[#A1A1AA] text-center py-8 text-sm">{l('No hay boosters importados. Usa el botón de arriba.', 'No boosters imported yet. Use the button above.')}</p>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
          {boosters.map(b => (
            <div key={b.booster_id} className="bg-[#12121A] border border-white/5 rounded-xl p-4">
              <div className="flex items-center gap-3 mb-2">
                {b.logo_url && <img src={b.logo_url} alt="" className="w-8 h-8 object-contain" />}
                <div className="flex-1 min-w-0">
                  <p className="text-white font-semibold text-sm truncate">{b.name}</p>
                  <p className="text-[#A1A1AA] text-xs">{b.tcg_type} · {b.set_code}</p>
                </div>
                <button onClick={() => openEdit(b)} className="p-1.5 rounded-lg text-[#C9A962] hover:bg-[#C9A962]/10" title={l('Editar', 'Edit')}><Edit2 size={13} /></button>
                <button onClick={() => handleDelete(b.booster_id)} className="p-1.5 rounded-lg text-[#EF4444] hover:bg-red-500/10"><Trash2 size={14} /></button>
              </div>
              <div className="flex justify-between text-xs text-[#A1A1AA]">
                <span>{b.total_cards || '?'} cards</span>
                <span>{b.release_date || ''}</span>
              </div>
              {editingBooster === b.booster_id && (
                <div className="mt-3 pt-3 border-t border-white/5 space-y-2">
                  <input value={editForm.name} onChange={e => setEditForm(f => ({...f, name: e.target.value}))} placeholder="Name" className={`w-full ${iclsInput}`} />
                  <div className="flex gap-2">
                    <input
                      value={editForm.release_date}
                      onChange={e => setEditForm(f => ({...f, release_date: e.target.value}))}
                      onClick={openNativeDatePicker}
                      onFocus={openNativeDatePicker}
                      placeholder="Release date"
                      type="date"
                      className={`flex-1 ${iclsInput}`}
                    />
                    <input value={editForm.total_cards} onChange={e => setEditForm(f => ({...f, total_cards: e.target.value}))} placeholder="Cards" type="number" className={`w-20 ${iclsInput}`} />
                  </div>
                  <div className="flex gap-2">
                    <button onClick={() => handleSaveEdit(b.booster_id)} className="flex-1 py-1.5 bg-[#C9A962] text-[#0A1612] rounded-lg text-xs font-semibold">{l('Guardar', 'Save')}</button>
                    <button onClick={() => setEditingBooster(null)} className="flex-1 py-1.5 bg-white/5 text-[#A1A1AA] rounded-lg text-xs">{l('Cancelar', 'Cancel')}</button>
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
